<script setup lang="ts">
import {nextTick, reactive, ref, useTemplateRef, watch} from "vue";
import {Button, Textarea, Select, ProgressSpinner} from "@/Components/Library";
import {useToast} from "primevue/usetoast";
import {analyzeQuestion, analyzeGroup} from "@/Services/analysis";
import Str from "@/Utils/Str";
import markdownit from "markdown-it";
import dayjs from "dayjs";
import User from "@/Models/User";
import {exportAiChat, getAiChat, getAiChats} from "@/Services/ai-chats";
import {AiChatSources} from "@/Enums";

type AiChat = {
    id: string;
    title: string;
    parent_id?: string | null;
    source?: 1 | 2 | 3 | null;
};

type AiChatMessage = {
    role: "user" | "ai";
    text: string;
};

const providers = ["XAI", "Gemini"];
const models = {
    xai: [
        {label: "4.1 Fast Non-Reasoning", value: "grok-4-1-fast-non-reasoning"},
        {label: "4.1 Fast Reasoning", value: "grok-4-1-fast-reasoning"},
        {label: "4 Fast Non-Reasoning", value: "grok-4-fast-non-reasoning"},
        {label: "4 Fast Reasoning", value: "grok-4-fast-reasoning"},
        {label: "3", value: "grok-3"},
        {label: "3 Mini", value: "grok-3-mini"},
    ],
    gemini: [
        {label: "2.0 Flash-Lite", value: "gemini-2.0-flash-lite"},
        {label: "2.0 Flash", value: "gemini-2.0-flash"},
        {label: "2.5 Flash-Lite", value: "gemini-2.5-flash-lite"},
        {label: "2.5 Flash", value: "gemini-2.5-flash"},
        {label: "2.5 Pro", value: "gemini-2.5-pro"},
        {label: "3 Pro (Preview)", value: "gemini-3-pro-preview"},
    ],
};

const toast = useToast();
const $user = new User();
const md = markdownit();

const {
    parentType,
    parentId,
    params = {},
} = defineProps<{
    parentType: "question" | "group";
    parentId: string;
    params?: {
        selectedGroups?: any[];
        dateFrom?: string | null;
        dateTo?: string | null;
        filter?: Record<string, unknown>;
        sort?: string[];
    };
}>();

const state = reactive({
    loading: {
        list: false,
        chat: false,
        response: false,
        exportChat: false,
    },
});

const chat = ref<AiChat | null>(null);
const messages = ref<AiChatMessage[]>([]);
const chats = ref<AiChat[]>([]);
const prompt = ref("");
const promptField = useTemplateRef("promptField");
const selectedProvider = ref("XAI");
const selectedModel = ref(
    models[selectedProvider.value.toLowerCase() as keyof typeof models][0].value ?? "grok-4-1-fast-non-reasoning",
);

const getChats = async () => {
    try {
        state.loading.list = true;
        const {data} = await getAiChats({
            parent_id: parentId,
            source: AiChatSources[parentType].value,
        });
        chats.value = data;
    } catch (e) {
        console.error(e);
    } finally {
        state.loading.list = false;
    }
};

const getChat = async (chatId: string) => {
    try {
        state.loading.chat = true;
        const {data} = await getAiChat(chatId);
        chat.value = data;
        messages.value = data.messages;
    } catch (e) {
        console.error(e);
    } finally {
        state.loading.chat = false;
    }
};

const analyze = async () => {
    if (!prompt.value.trim()) {
        toast.add({
            severity: "warn",
            summary: "Enter a prompt",
            detail: "Please write a prompt to analyze.",
            life: 3000,
        });
        return;
    }

    try {
        state.loading.response = true;
        const promptText = prompt.value;

        const requestData = {
            prompt: promptText,
            provider: selectedProvider.value,
            model: selectedModel.value,
            group_ids: params?.selectedGroups?.map((g: any) => g.id),
            date_from: params?.dateFrom ? dayjs(params.dateFrom).format("YYYY-MM-DD HH:mm:ss") : null,
            date_to: params?.dateTo ? dayjs(params.dateTo).format("YYYY-MM-DD HH:mm:ss") : null,
            filter: params?.filter,
            sort: params?.sort,
        };

        let response;
        if (parentType === "question") {
            response = await analyzeQuestion(parentId, chat.value?.id, requestData);
        } else {
            response = await analyzeGroup(parentId, chat.value?.id, requestData);
        }

        const {data} = response;
        prompt.value = "";
        messages.value.push({role: "user", text: promptText});
        messages.value.push(data);
        if (!chat.value) {
            chat.value = data.chat;
            chats.value.unshift(data.chat);
        }
        await nextTick();
        promptField.value?.$el?.scrollIntoView({behavior: "smooth", block: "start"});
    } catch (e) {
        console.error(e);
    } finally {
        state.loading.response = false;
    }
};

const exportChat = async () => {
    if (!chat.value) {
        toast.add({
            severity: "warn",
            summary: "No chat selected",
            detail: "Please select a chat to export.",
            life: 3000,
        });
        return;
    }

    try {
        state.loading.exportChat = true;
        const {path} = await exportAiChat(chat.value.id);
        window.location.href = route("files.download", path);

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Chat exported successfully.",
            life: 3000,
        });
    } catch (e) {
        console.error(e);
    } finally {
        state.loading.exportChat = false;
    }
};

const selectChat = async (chatId: string) => {
    if (chat.value?.id === chatId) {
        return;
    }

    await getChat(chatId);
};

const newChat = () => {
    chat.value = null;
    messages.value = [];
};

const init = async () => {
    if (parentId) {
        await getChats();
        if (chats.value.length > 0) {
            await getChat(chats.value[0].id);
        } else {
            newChat();
        }
    }
};
init();

watch(() => parentId, init);
</script>

<template>
    <div>
        <p class="mb-4 text-xs text-stone-600 dark:text-stone-400">
            Albie is an AI agent. Like all AI agents Albie’s responses may contain errors, hallucinations or incomplete
            reasoning. Do not rely on Albie’s responses for high-stakes decisions.
        </p>
        <div class="flex flex-col gap-8 md:flex-row" v-auto-animate>
            <div class="w-full max-w-xs flex-none">
                <div class="max-h-180 sticky top-0 flex flex-col gap-0.5 overflow-y-auto p-1" v-auto-animate>
                    <template v-for="chatItem in chats" :key="chatItem.id">
                        <Button
                            class="block w-full !justify-start rounded-md border border-transparent py-2 pl-3 pr-6 text-start font-normal hover:brightness-110"
                            :class="chatItem.id === chat?.id ? 'bg-white/10' : 'hover:border-stone-600'"
                            @click="selectChat(chatItem.id)"
                        >
                            {{ chatItem.title }}
                        </Button>
                    </template>
                    <Button
                        :icon="{icon: 'mdi:plus'}"
                        class="mt-4 border border-stone-600 text-white hover:brightness-110"
                        @click="newChat"
                    >
                        New chat
                    </Button>
                </div>
            </div>
            <div class="grow">
                <!--            <div class="mb-4 flex flex-col items-stretch gap-2">-->
                <!--                <Select v-model="selectedProvider" :options="providers" placeholder="Select Provider" class="grow" />-->
                <!--                <Select-->
                <!--                    v-model="selectedModel"-->
                <!--                    :options="models[selectedProvider.toLowerCase() as keyof typeof models]"-->
                <!--                    optionLabel="label"-->
                <!--                    optionValue="value"-->
                <!--                    placeholder="Select AI Model"-->
                <!--                    class="grow"-->
                <!--                />-->
                <!--            </div>-->
                <template v-if="state.loading.chat">
                    <div class="p-6 text-center">
                        <ProgressSpinner />
                    </div>
                </template>
                <template v-else>
                    <div v-if="!!messages.length" class="mb-6">
                        <div v-if="$user.can('export data')" class="mb-4 flex justify-end">
                            <Button
                                v-tooltip.left="`Export Chat`"
                                :icon="{icon: 'prime:download'}"
                                :loading="state.loading.exportChat"
                                @click="exportChat"
                            />
                        </div>
                        <template v-for="(message, i) in messages" :key="i">
                            <div
                                class="mb-6 p-3"
                                :class="[
                                    message.role === 'user'
                                        ? 'rounded-lg border border-stone-300 bg-stone-50 dark:border-stone-950 dark:bg-stone-950'
                                        : 'border-l-4 border-stone-700',
                                ]"
                            >
                                <div
                                    class="prose prose-stone w-full max-w-full dark:prose-invert"
                                    v-html="Str.of(md.render(message.text ?? 'There was an error.')).toString()"
                                ></div>
                            </div>
                        </template>
                    </div>
                    <form @submit.prevent="analyze" class="space-y-4">
                        <div>
                            <Textarea
                                ref="promptField"
                                id="analysis_prompt"
                                v-model="prompt"
                                class="w-full dark:!bg-stone-950"
                                auto-resize
                                fluid
                                placeholder="What would you like to know?"
                            />
                        </div>
                        <div class="flex justify-end gap-2">
                            <Button type="submit" class="bg-askr-green text-white" :loading="state.loading.response">
                                Send
                            </Button>
                        </div>
                    </form>
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
